// src/pages/driver/DriverTimesheet.jsx
import { useContext, useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { UserContext } from "../../contexts/UserContext";
import {
  ADMIN_STAFF_APPROVER_EMAIL,
  isUnseenLeaveDecision,
} from "../../utils/timesheetNotifications";
import {
  TIMEZONE,
  inputCls,
  emptyDay,
  toLocalDateString,
  getMonday,
  addDays,
  formatDisplayDate,
  getLeaveSubmittedText,
  getShiftPaidMinutes,
  formatHours,
  normalizeCompanyIds,
  statusBadgeClass,
} from "./driverTimesheet/helpers";
import SignaturePad from "./driverTimesheet/SignaturePad";
import NotificationPill from "./driverTimesheet/NotificationPill";

export default function DriverTimesheet() {
  const user = getAuth().currentUser;
  const { userInfo } = useContext(UserContext) || {};
  const role = String(userInfo?.role || "").toLowerCase();
  const isAdminStaff = role === "admin";
  const employeeLabel = isAdminStaff ? "Admin staff" : "Driver";
  const approverLabel = isAdminStaff ? "accountant" : "manager";
  const defaultName = user?.displayName || userInfo?.email || user?.email || "";

  const [weekStart, setWeekStart] = useState(() => toLocalDateString(getMonday()));
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("draft");
  const [signature, setSignature] = useState("");
  const [driverName, setDriverName] = useState("");
  const [submittedAtText, setSubmittedAtText] = useState("");
  const [managerNote, setManagerNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [timesheetHistory, setTimesheetHistory] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [markingLeaveDecisions, setMarkingLeaveDecisions] = useState(false);
  const [driverProfile, setDriverProfile] = useState(null);

  const [leaveForm, setLeaveForm] = useState({
    type: "Annual leave",
    startDate: toLocalDateString(new Date()),
    endDate: toLocalDateString(new Date()),
    reason: "",
    signature: "",
  });
  const [leaveSaving, setLeaveSaving] = useState(false);

  const weekDates = useMemo(() => {
    const monday = getMonday(`${weekStart}T00:00:00`);
    return Array.from({ length: 7 }, (_, index) => toLocalDateString(addDays(monday, index)));
  }, [weekStart]);

  const timesheetId = user?.uid && weekStart ? `${user.uid}_${weekStart}` : "";
  const isLocked = ["submitted", "approved"].includes(String(status).toLowerCase());
  const assignedCompanyIds = useMemo(
    () => normalizeCompanyIds(driverProfile || {}),
    [driverProfile],
  );

  const totals = useMemo(() => {
    const dayTotals = rows.map((row) => {
      const paidMinutes =
        getShiftPaidMinutes(row.startTime, row.finishTime, row.breakMinutes) +
        getShiftPaidMinutes(row.secondStartTime, row.secondFinishTime, row.secondBreakMinutes);
      return paidMinutes / 60;
    });

    return {
      dayTotals,
      weeklyTotal: dayTotals.reduce((sum, value) => sum + value, 0),
    };
  }, [rows]);

  const visibleTimesheetHistory = useMemo(() => {
    const term = historySearch.trim().toLowerCase();
    if (!term) return timesheetHistory;
    return timesheetHistory.filter((item) =>
      [item.weekStart, item.weekEnd, item.status, item.driverEmail, item.driverName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [historySearch, timesheetHistory]);

  const visibleLeaveHistory = useMemo(() => {
    const term = historySearch.trim().toLowerCase();
    if (!term) return leaveHistory;
    return leaveHistory.filter((item) =>
      [item.type, item.startDate, item.endDate, item.status, item.reason, getLeaveSubmittedText(item)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [historySearch, leaveHistory]);

  const leaveDecisionNotifications = useMemo(
    () => leaveHistory.filter(isUnseenLeaveDecision),
    [leaveHistory],
  );

  useEffect(() => {
    const loadDriverProfile = async () => {
      if (!user?.uid) return;
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        setDriverProfile(snap.exists() ? snap.data() : null);
      } catch (error) {
        console.warn("Failed to load user company assignments:", error);
        setDriverProfile(null);
      }
    };

    loadDriverProfile();
  }, [user?.uid]);

  useEffect(() => {
    const loadTimesheet = async () => {
      if (!user || !timesheetId) return;

      setLoading(true);
      setMessage("");

      try {
        const snap = await getDoc(doc(db, "timesheets", timesheetId));
        const baseRows = weekDates.map((date) => emptyDay(date));

        if (snap.exists()) {
          const data = snap.data();
          const savedRows = Array.isArray(data.days) ? data.days : [];
          const mergedRows = baseRows.map((baseRow) => {
            const savedRow = savedRows.find((row) => row.date === baseRow.date);
            return savedRow ? { ...baseRow, ...savedRow } : baseRow;
          });

          setRows(mergedRows);
          setStatus(data.status || "draft");
          setSignature(data.signature || "");
          setDriverName(data.driverName || data.employeeName || defaultName);
          setSubmittedAtText(data.submittedAtText || "");
          setManagerNote(data.managerNote || "");
        } else {
          setRows(baseRows);
          setStatus("draft");
          setSignature("");
          setDriverName(defaultName);
          setSubmittedAtText("");
          setManagerNote("");
        }
      } catch (error) {
        console.error("Failed to load timesheet:", error);
        setMessage("Could not load this timesheet. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadTimesheet();
  }, [defaultName, user, timesheetId, weekDates]);

  useEffect(() => {
    const loadHistory = async () => {
      if (!user) return;
      try {
        const timesheetSnap = await getDocs(
          query(collection(db, "timesheets"), where("driverId", "==", user.uid)),
        );
        const timesheets = timesheetSnap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => String(b.weekStart || "").localeCompare(String(a.weekStart || "")))
          .slice(0, 12);
        setTimesheetHistory(timesheets);

        const leaveSnap = await getDocs(
          query(collection(db, "leaveRequests"), where("driverId", "==", user.uid)),
        );
        const leaves = leaveSnap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => String(b.startDate || "").localeCompare(String(a.startDate || "")));
        setLeaveHistory(leaves);
      } catch (error) {
        console.error("Failed to load timesheet/leave history:", error);
      }
    };

    loadHistory();
  }, [user, status, leaveSaving]);

  const updateRow = (index, field, value) => {
    setRows((currentRows) =>
      currentRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const validateBeforeSubmit = () => {
    const hasAnyTime = rows.some((row) =>
      row.startTime || row.finishTime || row.secondStartTime || row.secondFinishTime,
    );
    const incompleteRows = rows.filter((row) =>
      (row.startTime && !row.finishTime) ||
      (!row.startTime && row.finishTime) ||
      (row.secondStartTime && !row.secondFinishTime) ||
      (!row.secondStartTime && row.secondFinishTime),
    );

    if (!hasAnyTime) return "Please enter at least one start and finish time before submitting.";
    if (incompleteRows.length) return "Some shifts have only start or finish time. Please complete both fields or clear the shift.";
    if (!signature) return "Please sign the timesheet before submitting.";
    return "";
  };

  const saveTimesheet = async (nextStatus = "draft") => {
    if (!user || !timesheetId) return;

    if (nextStatus === "submitted") {
      const validationMessage = validateBeforeSubmit();
      if (validationMessage) {
        setMessage(validationMessage);
        return;
      }
    }

    setSaving(true);
    setMessage("");

    const submittedNow = new Date().toLocaleString("en-AU", {
      timeZone: TIMEZONE,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    try {
      await setDoc(
        doc(db, "timesheets", timesheetId),
        {
          driverId: user.uid,
          driverEmail: user.email || "",
          driverName: driverName.trim() || defaultName || employeeLabel,
          employeeName: driverName.trim() || defaultName || employeeLabel,
          submitterRole: role || "driver",
          approverEmail: isAdminStaff ? ADMIN_STAFF_APPROVER_EMAIL : "",
          companyIds: assignedCompanyIds,
          companyId: assignedCompanyIds[0] || "",
          weekStart,
          weekEnd: weekDates[6],
          days: rows.map((row, index) => ({
            ...row,
            totalHours: Number(totals.dayTotals[index].toFixed(2)),
          })),
          totalHours: Number(totals.weeklyTotal.toFixed(2)),
          status: nextStatus,
          signature,
          managerNote,
          submittedAt: nextStatus === "submitted" ? serverTimestamp() : null,
          submittedAtText: nextStatus === "submitted" ? submittedNow : submittedAtText,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      setStatus(nextStatus);
      if (nextStatus === "submitted") setSubmittedAtText(submittedNow);
      setMessage(nextStatus === "submitted" ? `Timesheet submitted for ${approverLabel} approval.` : "Timesheet saved as draft.");
    } catch (error) {
      console.error("Failed to save timesheet:", error);
      setMessage("Could not save this timesheet. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const submitLeaveRequest = async (event) => {
    event.preventDefault();
    if (!user) return;

    if (!leaveForm.startDate || !leaveForm.endDate) {
      setMessage("Please select leave start and end dates.");
      return;
    }

    if (leaveForm.endDate < leaveForm.startDate) {
      setMessage("Leave end date cannot be before the start date.");
      return;
    }

    if (!leaveForm.signature) {
      setMessage("Please sign the leave request before submitting.");
      return;
    }

    setLeaveSaving(true);
    setMessage("");

    const submittedNow = new Date().toLocaleString("en-AU", {
      timeZone: TIMEZONE,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    try {
      await addDoc(collection(db, "leaveRequests"), {
        driverId: user.uid,
        driverEmail: user.email || "",
        driverName: driverName.trim() || defaultName || employeeLabel,
        employeeName: driverName.trim() || defaultName || employeeLabel,
        submitterRole: role || "driver",
        approverEmail: isAdminStaff ? ADMIN_STAFF_APPROVER_EMAIL : "",
        companyIds: assignedCompanyIds,
        companyId: assignedCompanyIds[0] || "",
        type: leaveForm.type,
        startDate: leaveForm.startDate,
        endDate: leaveForm.endDate,
        reason: leaveForm.reason.trim(),
        status: "pending",
        signature: leaveForm.signature,
        driverDecisionSeenAt: null,
        submittedAtText: submittedNow,
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setLeaveForm((current) => ({ ...current, reason: "", signature: "" }));
      setMessage(`Leave request submitted for ${approverLabel} approval.`);
    } catch (error) {
      console.error("Failed to submit leave request:", error);
      setMessage("Could not submit leave request. Please try again.");
    } finally {
      setLeaveSaving(false);
    }
  };

  const markLeaveDecisionsSeen = async () => {
    if (!leaveDecisionNotifications.length) return;

    setMarkingLeaveDecisions(true);
    setMessage("");

    try {
      await Promise.all(
        leaveDecisionNotifications.map((item) =>
          updateDoc(doc(db, "leaveRequests", item.id), {
            driverDecisionSeenAt: serverTimestamp(),
          }),
        ),
      );

      setLeaveHistory((current) =>
        current.map((item) =>
          isUnseenLeaveDecision(item)
            ? { ...item, driverDecisionSeenAt: new Date().toISOString() }
            : item,
        ),
      );
      setMessage("Leave request updates marked as seen.");
    } catch (error) {
      console.error("Failed to mark leave request updates as seen:", error);
      setMessage("Could not mark leave request updates as seen. Please try again.");
    } finally {
      setMarkingLeaveDecisions(false);
    }
  };

  const openPrintableTimesheet = (item = null) => {
    const data = item || {
      driverName: driverName.trim() || user?.displayName || user?.email || "Driver",
      driverEmail: user?.email || "",
      weekStart,
      weekEnd: weekDates[6],
      days: rows.map((row, index) => ({ ...row, totalHours: totals.dayTotals[index] })),
      totalHours: totals.weeklyTotal,
      status,
      signature,
      submittedAtText,
      managerNote,
    };

    const rowsHtml = (Array.isArray(data.days) ? data.days : [])
      .map(
        (day) => `
          <tr>
            <td>${formatDisplayDate(day.date)}<br/><small>${day.date || ""}</small></td>
            <td>${day.startTime || "-"}</td>
            <td>${day.finishTime || "-"}</td>
            <td>${day.breakMinutes || "0"}</td>
            <td>${day.secondStartTime || "-"}</td>
            <td>${day.secondFinishTime || "-"}</td>
            <td>${day.secondBreakMinutes || "0"}</td>
            <td>${formatHours(day.totalHours)}</td>
            <td>${day.notes || ""}</td>
          </tr>
        `,
      )
      .join("");

    const signatureHtml = data.signature
      ? `<img src="${data.signature}" alt="Driver signature" style="max-width: 280px; max-height: 90px; border: 1px solid #ddd; border-radius: 8px;" />`
      : "No signature";

    const popup = window.open("", "_blank", "width=1000,height=800");
    popup.document.write(`
      <html>
        <head>
          <title>Timesheet ${data.weekStart || ""}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111827; padding: 28px; }
            h1 { margin: 0 0 4px; font-size: 24px; }
            .muted { color: #6b7280; font-size: 13px; }
            .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin: 18px 0; }
            .box { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 14px; }
            th, td { border: 1px solid #e5e7eb; padding: 9px; text-align: left; vertical-align: top; font-size: 13px; }
            th { background: #f3f4f6; }
            .footer { margin-top: 20px; display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
            @media print { button { display: none; } body { padding: 0; } }
          </style>
        </head>
        <body>
          <button onclick="window.print()" style="margin-bottom:16px;padding:10px 14px;border-radius:10px;border:1px solid #ddd;background:#111827;color:#fff;font-weight:700;">Print / Save PDF</button>
          <h1>Greenspace Industries Timesheet</h1>
          <div class="muted">Manual weekly timesheet for manager approval</div>
          <div class="meta">
            <div class="box"><strong>Driver</strong><br/>${data.driverName || "Driver"}<br/><span class="muted">${data.driverEmail || ""}</span></div>
            <div class="box"><strong>Week</strong><br/>${data.weekStart || ""} to ${data.weekEnd || ""}<br/><span class="muted">Status: ${data.status || "draft"}</span></div>
            <div class="box"><strong>Total hours</strong><br/>${formatHours(data.totalHours)}</div>
            <div class="box"><strong>Submitted</strong><br/>${data.submittedAtText || "Not submitted"}</div>
          </div>
          <table>
            <thead><tr><th>Day</th><th>Start</th><th>Finish</th><th>Break</th><th>Start</th><th>Finish</th><th>Break</th><th>Hours</th><th>Notes</th></tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <div class="footer">
            <div class="box"><strong>Driver signature</strong><br/><br/>${signatureHtml}</div>
            <div class="box"><strong>Manager note</strong><br/><br/>${data.managerNote || ""}</div>
          </div>
        </body>
      </html>
    `);
    popup.document.close();
  };

  const openPrintableLeave = (item) => {
    const signatureHtml = item.signature
      ? `<img src="${item.signature}" alt="Driver signature" style="max-width: 280px; max-height: 90px; border: 1px solid #ddd; border-radius: 8px;" />`
      : "No signature";
    const submittedText = getLeaveSubmittedText(item) || "Not available";
    const popup = window.open("", "_blank", "width=900,height=700");
    popup.document.write(`
      <html>
        <head>
          <title>Leave Request ${item.startDate || ""}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #111827; padding: 28px; }
            .box { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin: 12px 0; }
            .muted { color: #6b7280; font-size: 13px; }
            @media print { button { display: none; } body { padding: 0; } }
          </style>
        </head>
        <body>
          <button onclick="window.print()" style="margin-bottom:16px;padding:10px 14px;border-radius:10px;border:1px solid #ddd;background:#111827;color:#fff;font-weight:700;">Print / Save PDF</button>
          <h1>Greenspace Industries Leave Request</h1>
          <div class="box"><strong>Driver</strong><br/>${item.driverName || "Driver"}<br/><span class="muted">${item.driverEmail || ""}</span></div>
          <div class="box"><strong>Type</strong><br/>${item.type || ""}</div>
          <div class="box"><strong>Dates</strong><br/>${item.startDate || ""} to ${item.endDate || ""}</div>
          <div class="box"><strong>Submitted</strong><br/>${submittedText}</div>
          <div class="box"><strong>Status</strong><br/>${item.status || "pending"}</div>
          <div class="box"><strong>Reason / note</strong><br/>${item.reason || ""}</div>
          <div class="box"><strong>Driver signature</strong><br/><br/>${signatureHtml}</div>
          <div class="box"><strong>Manager note</strong><br/>${item.managerNote || ""}</div>
        </body>
      </html>
    `);
    popup.document.close();
  };

  if (!user) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-sm text-gray-500">Please log in to view your timesheet.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-sm text-gray-500">Loading your timesheet...</div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-4 sm:space-y-6 text-gray-900">
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3 sm:px-6">
          <div className="flex flex-wrap items-baseline gap-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
              {employeeLabel}
            </span>
            <h1 className="text-lg font-semibold tracking-tight text-gray-950">
              Timesheet &amp; leave request
            </h1>
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${statusBadgeClass(status)}`}>
              {status}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            <span className="font-semibold text-gray-900">{formatHours(totals.weeklyTotal)}</span> hours this week
          </div>
        </div>

        <div className="px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">{employeeLabel} name</label>
                <input
                  type="text"
                  value={driverName}
                  onChange={(event) => setDriverName(event.target.value)}
                  placeholder="Your name"
                  disabled={isLocked}
                  className={`${inputCls} mt-1`}
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Select week</label>
                <input
                  type="date"
                  value={weekStart}
                  onChange={(event) => setWeekStart(toLocalDateString(getMonday(`${event.target.value}T00:00:00`)))}
                  className={`${inputCls} mt-1`}
                />
                <p className="mt-1 text-xs text-gray-500">Any selected date opens that Monday to Sunday week.</p>
              </div>
              <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                No live clock in/out. This is manual entry only, so staff can complete it like the paper timesheet.
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => openPrintableTimesheet()}
                className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50"
              >
                Print / Save PDF
              </button>
              <button
                onClick={() => saveTimesheet("draft")}
                disabled={saving || isLocked}
                className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save draft"}
              </button>
              <button
                onClick={() => saveTimesheet("submitted")}
                disabled={saving || isLocked}
                className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Submit for approval
              </button>
            </div>
          </div>

          {message && (
            <div className="mt-4 rounded-2xl border border-emerald-100 bg-white/80 px-4 py-3 text-sm font-medium text-gray-700 shadow-sm">
              {message}
            </div>
          )}

          {managerNote && (
            <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              <span className="font-semibold">Manager note:</span> {managerNote}
            </div>
          )}
        </div>
      </div>

      {leaveDecisionNotifications.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-amber-950">Leave request update</h2>
                <NotificationPill count={leaveDecisionNotifications.length} />
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                {leaveDecisionNotifications.slice(0, 3).map((item) => (
                  <div key={item.id} className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm shadow-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-gray-900">{item.type || "Leave request"}</span>
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${statusBadgeClass(item.status)}`}>{item.status}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-600">{item.startDate} to {item.endDate}</div>
                    <div className="mt-1 text-xs text-gray-600">
                      Submitted: {getLeaveSubmittedText(item) || "Not available"}
                    </div>
                    {item.managerNote && <div className="mt-2 text-xs text-gray-700">Manager note: {item.managerNote}</div>}
                  </div>
                ))}
              </div>
              {leaveDecisionNotifications.length > 3 && (
                <p className="mt-2 text-xs font-medium text-amber-900">
                  {leaveDecisionNotifications.length - 3} more leave request updates are in your history.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={markLeaveDecisionsSeen}
              disabled={markingLeaveDecisions}
              className="rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {markingLeaveDecisions ? "Marking..." : "Mark as seen"}
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 sm:p-4">
        <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Weekly hours</h2>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">Enter one or two shifts, break minutes, and notes for each day.</p>
          </div>
          <span className="text-xs font-semibold text-gray-600">Total: {formatHours(totals.weeklyTotal)} hours</span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1180px] w-full border-separate border-spacing-y-2 text-left text-sm">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                <th className="px-3 py-2">Day</th>
                <th className="px-3 py-2">Start</th>
                <th className="px-3 py-2">Finish</th>
                <th className="px-3 py-2">Break</th>
                <th className="px-3 py-2">Start</th>
                <th className="px-3 py-2">Finish</th>
                <th className="px-3 py-2">Break</th>
                <th className="px-3 py-2">Hours</th>
                <th className="px-3 py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.date} className="rounded-2xl bg-gray-50 align-top shadow-sm">
                  <td className="rounded-l-2xl px-3 py-3 font-semibold text-gray-900">
                    <div>{formatDisplayDate(row.date)}</div>
                    <div className="mt-0.5 text-xs font-normal text-gray-500">{row.date}</div>
                  </td>
                  <td className="px-3 py-3">
                    <input type="time" value={row.startTime} disabled={isLocked} onChange={(event) => updateRow(index, "startTime", event.target.value)} className={inputCls} />
                  </td>
                  <td className="px-3 py-3">
                    <input type="time" value={row.finishTime} disabled={isLocked} onChange={(event) => updateRow(index, "finishTime", event.target.value)} className={inputCls} />
                  </td>
                  <td className="px-3 py-3">
                    <input type="number" min="0" step="5" value={row.breakMinutes} disabled={isLocked} onChange={(event) => updateRow(index, "breakMinutes", event.target.value)} className={inputCls} />
                  </td>
                  <td className="px-3 py-3">
                    <input type="time" value={row.secondStartTime} disabled={isLocked} onChange={(event) => updateRow(index, "secondStartTime", event.target.value)} className={inputCls} />
                  </td>
                  <td className="px-3 py-3">
                    <input type="time" value={row.secondFinishTime} disabled={isLocked} onChange={(event) => updateRow(index, "secondFinishTime", event.target.value)} className={inputCls} />
                  </td>
                  <td className="px-3 py-3">
                    <input type="number" min="0" step="5" value={row.secondBreakMinutes} disabled={isLocked} onChange={(event) => updateRow(index, "secondBreakMinutes", event.target.value)} className={inputCls} />
                  </td>
                  <td className="px-3 py-3 font-semibold text-gray-900">{formatHours(totals.dayTotals[index])}</td>
                  <td className="rounded-r-2xl px-3 py-3">
                    <input type="text" value={row.notes} disabled={isLocked} onChange={(event) => updateRow(index, "notes", event.target.value)} placeholder="Optional note" className={inputCls} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 sm:p-4">
        <div className="mb-3">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Driver signature</h2>
          <p className="mt-1 text-xs sm:text-sm text-gray-500">Signature is required before submitting the timesheet.</p>
        </div>
        <SignaturePad value={signature} onChange={setSignature} disabled={isLocked} />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 sm:p-4">
        <div className="mb-3">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Leave request</h2>
          <p className="mt-1 text-xs sm:text-sm text-gray-500">Submit annual leave, sick leave, or unavailable days for {approverLabel} approval.</p>
        </div>

        <form onSubmit={submitLeaveRequest} className="grid gap-3 lg:grid-cols-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Type</label>
            <select value={leaveForm.type} onChange={(event) => setLeaveForm((current) => ({ ...current, type: event.target.value }))} className={`${inputCls} mt-1`}>
              <option>Annual leave</option>
              <option>Sick leave</option>
              <option>Personal leave</option>
              <option>Unavailable</option>
              <option>Other</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Start date</label>
            <input type="date" value={leaveForm.startDate} onChange={(event) => setLeaveForm((current) => ({ ...current, startDate: event.target.value }))} className={`${inputCls} mt-1`} />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">End date</label>
            <input type="date" value={leaveForm.endDate} onChange={(event) => setLeaveForm((current) => ({ ...current, endDate: event.target.value }))} className={`${inputCls} mt-1`} />
          </div>

          <div className="lg:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Reason / note</label>
            <input type="text" value={leaveForm.reason} onChange={(event) => setLeaveForm((current) => ({ ...current, reason: event.target.value }))} placeholder="Optional" className={`${inputCls} mt-1`} />
          </div>

          <div className="lg:col-span-5">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Leave request signature</label>
            <SignaturePad value={leaveForm.signature} onChange={(value) => setLeaveForm((current) => ({ ...current, signature: value }))} />
          </div>

          <div className="lg:col-span-5 flex justify-end">
            <button type="submit" disabled={leaveSaving} className="rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60">
              {leaveSaving ? "Submitting..." : "Submit leave request"}
            </button>
          </div>
        </form>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-3 sm:p-4">
        <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Search previous records</h2>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">Find old timesheets and leave requests, then print or save a PDF copy anytime.</p>
          </div>
          <input
            value={historySearch}
            onChange={(event) => setHistorySearch(event.target.value)}
            placeholder="Search date, status, leave type..."
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100 lg:max-w-sm"
          />
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-900">Timesheets</h3>
            <div className="space-y-2">
              {visibleTimesheetHistory.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-6 text-sm text-gray-500">No timesheets found.</div>
              ) : (
                visibleTimesheetHistory.map((item) => (
                  <div key={item.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-semibold text-gray-900">{item.weekStart} to {item.weekEnd}</div>
                        <div className="mt-0.5 text-xs text-gray-500">Total: {formatHours(item.totalHours)} hours</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${statusBadgeClass(item.status)}`}>{item.status || "draft"}</span>
                        <button type="button" onClick={() => openPrintableTimesheet(item)} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50">PDF</button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Leave requests</h3>
              <NotificationPill count={leaveDecisionNotifications.length} />
            </div>
            <div className="space-y-2">
              {visibleLeaveHistory.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-6 text-sm text-gray-500">No leave requests found.</div>
              ) : (
                visibleLeaveHistory.map((item) => (
                  <div key={item.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-semibold text-gray-900">{item.type}</div>
                        <div className="mt-0.5 text-xs text-gray-500">{item.startDate} to {item.endDate}</div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          Submitted: {getLeaveSubmittedText(item) || "Not available"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${statusBadgeClass(item.status)}`}>{item.status || "pending"}</span>
                        <button type="button" onClick={() => openPrintableLeave(item)} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50">PDF</button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
